import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CasinoTagsEnum,
  getSectionGameImage,
  getSectionGameKey,
  getSectionGameLabel,
  getSectionGameNavigationPath,
  isProviderCategorySectionEntry,
  SectionGame,
  useHomeSectionGames,
  HOME_ROUTE,
  INT_CASINO_ROUTE,
} from "@hypexone/uicore";
import { SVLS_API_CLIENT } from "../../../App.utils";

const SKY_GIFS_SECTION_CODE = "sky_gifs";

interface HomeGamesProps {
  isLoggedIn: boolean;
  showPopUp: () => void;
}

const HomeGames: React.FC<HomeGamesProps> = ({ isLoggedIn, showPopUp }) => {
  const { games: trendingGames } = useHomeSectionGames(
    SVLS_API_CLIENT,
    CasinoTagsEnum.TRENDING
  );
  const { games: newLaunchGames } = useHomeSectionGames(
    SVLS_API_CLIENT,
    CasinoTagsEnum.NEW_LAUNCH + "_sky"
  );
  const { games: recommendedGames } = useHomeSectionGames(
    SVLS_API_CLIENT,
    CasinoTagsEnum.RECOMMENDED_GAMES
  );
  const { games: liveCasinoGames } = useHomeSectionGames(
    SVLS_API_CLIENT,
    CasinoTagsEnum.LIVE_CASINO_GAMES
  );
  const { games: slotsGames } = useHomeSectionGames(
    SVLS_API_CLIENT,
    CasinoTagsEnum.SLOTS
  );
  const { games: skyGifsGames } = useHomeSectionGames(
    SVLS_API_CLIENT,
    SKY_GIFS_SECTION_CODE
  );
  const navigate = useNavigate();

  let loggedInUserStatus;
  if (isLoggedIn) {
    loggedInUserStatus = JSON.parse(
      window.atob(sessionStorage.getItem("jwt_token").split(".")[1])
    ).status;
  }

  const handleGameClick = (game: SectionGame) => {
    const path = getSectionGameNavigationPath(
      game,
      INT_CASINO_ROUTE,
      "subProvider"
    );
    if (!path) return;

    if (isProviderCategorySectionEntry(game)) {
      navigate(path);
      return;
    }

    if (!isLoggedIn) {
      showPopUp();
      return;
    }

    if (loggedInUserStatus === 0 || loggedInUserStatus === 3) {
      navigate(HOME_ROUTE);
      return;
    }

    if (game.providerName === "Indian Casino") {
      navigate(`/casino/indian/${game.gameCode}`);
      return;
    }

    navigate(path, { state: { gameName: game.gameName } });
  };

  const SkyGifCard = ({ game }: { game: SectionGame }) => {
    const imageUrl = getSectionGameImage(game);
    const title = game.gameName || game.category.toLowerCase() != 'all' ? game.category : game.sectionProvider;
    const onClick = () => handleGameClick(game);

    return (
      <div className="relative">
        <img
          src={imageUrl}
          alt={title}
          className="w-[100%] h-[100px] sm:h-[113px] md:h-auto cursor-pointer object-cover"
          onClick={onClick}
        />
        <div className="flex w-full h-[33px] flex-shrink-0 absolute bottom-0 left-0 right-0 border-b-4 border-goldenYellow">
          <div
            className="flex flex-1 items-center bg-gradient-to-r from-black to-transparent text-white pl-2 pr-1 py-1 text-xs md:text-sm font-bold cursor-pointer min-w-0 uppercase break-words"
            onClick={onClick}
          >
            {title}
          </div>
          <button
            type="button"
            className="flex items-center justify-center flex-shrink-0 bg-goldenYellow text-black px-2 pl-4 py-1 text-[11px] font-bold cursor-pointer min-w-[70px] [clip-path:polygon(15%_0,100%_0,100%_100%,0_100%)] hover:opacity-90"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            Play Now
          </button>
        </div>
      </div>
    );
  };

  const GameCard = ({ game }: { game: SectionGame }) => {
    const launchGame = () => handleGameClick(game);
    return (
      <div className="flex flex-col flex-shrink-0 w-[38%] md:w-[18%] lg:w-[15%] xl:w-[12%] 2xl:w-[10%] h-[210px] md:h-[190px]">
        <div className="relative overflow-hidden flex-1">
          <img
            loading="lazy"
            src={getSectionGameImage(game)}
            alt={getSectionGameLabel(game)}
            className="w-full h-full cursor-pointer object-cover"
            onClick={launchGame}
          />
        <div className="flex w-full h-[32px] sm:h-[24px] flex-shrink-0 absolute bottom-0 left-0 right-0 border-b-4 border-[#ffb80c]">
          <div
            className="flex flex-1 items-center bg-gradient-to-r from-black to-transparent text-white pl-2 pr-1 py-1 text-[11px] font-bold cursor-pointer min-w-0"
            onClick={launchGame}
          >
            <span className="truncate">{getSectionGameLabel(game)}</span>
          </div>
          <button
            type="button"
            className="flex items-center justify-center flex-shrink-0 bg-goldenYellow text-black px-2 pl-4 py-1 text-[11px] font-bold cursor-pointer min-w-[70px] [clip-path:polygon(15%_0,100%_0,100%_100%,0_100%)] hover:opacity-90"
            onClick={(e) => {
              e.stopPropagation();
              launchGame();
            }}
          >
            Play Now
          </button>
        </div>
        </div>
      </div>
    );
  };

  const CategorySection = ({
    title,
    games,
  }: {
    title: string;
    games: SectionGame[];
  }) => (
    <div className="flex flex-col gap-0.5">
      <h2
        // style={{
        //   background: "linear-gradient(-180deg, #F72424 0%, #BB1C00 100%)",
        // }}
        className="text-[13px] md:text-[16px] px-1.5 py-1 font-bold bg-goldenYellow text-black"
      >
        {title}
      </h2>
      <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar">
        {games?.map((game, gameIndex) => (
          <GameCard
            key={(game.gameId || game.id?.toString() || "") + gameIndex}
            game={game}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-0.5">
      {skyGifsGames.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-2 gap-0.5">
          {skyGifsGames.map((game) => (
            <SkyGifCard key={getSectionGameKey(game)} game={game} />
          ))}
        </div>
      )}
      {newLaunchGames.length > 0 && (
        <CategorySection title="NEW LAUNCH" games={newLaunchGames} />
      )}
      {trendingGames.length > 0 && (
        <CategorySection title="TRENDING GAMES" games={trendingGames} />
      )}
      {recommendedGames.length > 0 && (
        <CategorySection title="RECOMMENDED GAMES" games={recommendedGames} />
      )}
      {liveCasinoGames.length > 0 && (
        <CategorySection title="LIVE CASINO GAMES" games={liveCasinoGames} />
      )}
      {slotsGames.length > 0 && (
        <CategorySection title="SLOTS" games={slotsGames} />
      )}
    </div>
  );
};

export default HomeGames;
